import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DefaultButton, Link } from '@bingads-webui-react/fluent-controls';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';

import { ReactTemplate } from '@bingads-webui-react/react-template';

const DEFAULT_MESSAGE = _TL_('Conversion tracking must be configured for your bid strategy to be effective. Without configured conversion tracking, your bid strategy will automatically switch to maximizing clicks instead of optimizing for conversions, which may not be ideal for your campaign performance.');
const TRANSITION_MESSAGE = _TL_('Once we track a sufficient amount of conversion data, your Performance Max campaigns will automatically switch to your selected conversion-based bid strategy. You can always change the bid strategy in the campaign\'s settings. {{link}}Learn more about bid strategy for Performance Max campaigns{{/link}}');

const getTemplateToDisplay = (i18n, usesMaxClicks, isCampaignCreation, hasActiveGoal) => {
  if (_.isUndefined(hasActiveGoal)) {
    return null;
  }

  if (isCampaignCreation) {
    if (!hasActiveGoal) {
      return { message: i18n.getString(DEFAULT_MESSAGE), type: 'warning' };
    }
    return null;
  }

  if (usesMaxClicks) {
    if (!hasActiveGoal) {
      return { message: i18n.getString(DEFAULT_MESSAGE), type: 'warning' };
    }
    return { message: i18n.getString(TRANSITION_MESSAGE), type: 'info' };
  }

  return null;
};

const getUsesMaxClicks = campaign => _.chain(campaign)
  .get(['data', 'CampaignSettings'], [])
  .where({ '@odata.type': '#Model.PerformanceMaxSetting' })
  .first()
  .get('UseMaxClicks')
  .value();

const getHasActiveGoal = conversionGoalsDetails => _.get(conversionGoalsDetails, 'IsActiveConversionGoalPresent');

// TODO(orlandom): Remove when the new Big Strategy component is ready
export const ConversionBasedBiddingBanner = (props) => {
  const {
    i18n,
    scope,
    campaign,
    conversionGoalsDetails,
    isCampaignCreation,
  } = props;

  const usesMaxClicks = useMemo(() => getUsesMaxClicks(campaign), [campaign]);
  const hasActiveGoal = useMemo(() => getHasActiveGoal(conversionGoalsDetails), [conversionGoalsDetails]);

  const messageSettings = useMemo(
    () => getTemplateToDisplay(i18n, usesMaxClicks, isCampaignCreation, hasActiveGoal),
    [i18n, usesMaxClicks, isCampaignCreation, hasActiveGoal]
  );

  const onSetConversionTracking = useCallback(() => {
    scope.save('/campaign/vnext/conversiongoals/create');
  }, [scope]);

  if (!messageSettings) {
    return null;
  }

  return (
    <MessageBar
      intent={messageSettings.type}
      layout="multiline"
      className="conversion-based-bidding-banner"
      style={{
        minHeight: '32px',
        width: '100%',
        wordBreak: 'break-word',
        marginTop: '12px',
      }}
    >
      <MessageBarBody>
        <div className="conversion-based-bidding-banner-message">
          <ReactTemplate
            template={messageSettings.message}
            model={{
              b: text => <b>{text}</b>,
              link: text => (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <Link className="HelpTopic" query="help:ext56786">{text}</Link>
              ),
            }}
          />
        </div>
        {(!isCampaignCreation && !hasActiveGoal) && (
          <div disableShrink>
            <DefaultButton
              className="set-conversion-goal"
              text={i18n.getString(_TL_('Set a conversion goal'))}
              onClick={onSetConversionTracking}
            />
          </div>
        )}
      </MessageBarBody>
    </MessageBar >
  );
};

ConversionBasedBiddingBanner.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  campaign: PropTypes.shape({}).isRequired,
  conversionGoalsDetails: PropTypes.shape({}).isRequired,
  isCampaignCreation: PropTypes.bool.isRequired,
};
